import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { VAULTS_NS as NS } from './constants';
import { GROUP_CHAT_ID_MAX, X_HANDLE_MAX } from './contact';
import type { VaultContactForm } from './useVaultContact';

/**
 * XBIT-4401 (C-06 amendment "contact links"; PO 2026-10-05): bind or update a vault's X handle and group chat. One
 * dialog for Vault Management and the applications list, prefilled from the catalog row read when it opens; a field left
 * empty clears that link.
 */
export const ContactDialog = ({ form }: { form: VaultContactForm }) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) => translate(`${NS}.contact.${key}`, options);
  const { target } = form;
  const link = (
    name: 'xHandle' | 'groupChatId',
    value: string,
    set: (v: string) => void,
    max: number
  ) => {
    const error = form.errors[name];
    return (
      <TextField
        fullWidth
        id={`vault-contact-${name}`}
        label={t(name)}
        value={value}
        error={Boolean(error)}
        disabled={form.loading || form.readFailed}
        helperText={[
          error && translate(error, { max }),
          t('hint', { count: Array.from(value.trim()).length, max }),
        ]
          .filter(Boolean)
          .join(' · ')}
        onChange={(e) => set(e.target.value)}
      />
    );
  };

  return (
    <Dialog
      open={target !== null}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      {target && (
        <>
          <DialogTitle>{t('title', { name: target.name })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">{t('body')}</Typography>
              {form.alert && (
                <Alert severity="error">
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                    {form.alert.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Stack>
                </Alert>
              )}
              {link('xHandle', form.xHandle, form.setXHandle, X_HANDLE_MAX)}
              {link('groupChatId', form.groupChatId, form.setGroupChatId, GROUP_CHAT_ID_MAX)}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={form.close} disabled={form.submitting}>
              {t('cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={form.submit}
              disabled={form.submitting || form.loading || form.readFailed}
              loading={form.submitting || form.loading}
            >
              {form.alert && !form.readFailed ? t('retry') : t('save')}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
